import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  constructor(private title: Title, private meta: Meta, @Inject(DOCUMENT) private document: Document) { }

  updateTitle(title: string): void {
    if (title) {
      this.title.setTitle(title);
    }
  }

  updateDescription(description: string): void {
    if (description) {
      this.meta.updateTag({ name: 'description', content: description });
    }
  }

  // The canonical tag is a <link> element, which the Angular Meta service
  // cannot manage (it only handles <meta> tags), so we update the DOM directly.
  setCanonicalTag(newCanonicalUrl: string): void {
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', newCanonicalUrl);
  }

  setSeo(url: string) {
    const seoData = {
      '/': {
        title: 'algoleague | Programming Contests and Training',
        description: 'Join algoleague to participate in coding contests, solve algorithmic problems, and access training resources to improve your programming skills.',
        staticContent: `
          <h1>algoleague | Programming Contests and Training</h1>
          <p>Join algoleague to participate in coding contests, solve algorithmic problems, and access training resources to improve your programming skills.</p>
        `
      },
      '/problem/list': {
        title: 'algoleague | Problem List',
        description: 'Explore a wide range of algorithmic problems on algoleague to enhance your coding skills.',
        staticContent: `
          <h1>algoleague | Problem List</h1>
          <p>Browse and solve algorithmic problems on algoleague to improve your programming and problem-solving skills.</p>
        `
      },
      '/contest/list': {
        title: 'algoleague | Programming Contests',
        description: 'Join algoleague’s programming contests to compete, test your skills, and rank on the leaderboard.',
        staticContent: `
          <h1>algoleague | Programming Contests</h1>
          <p>Participate in exciting programming contests on algoleague, compete individually or in teams, and climb the global leaderboard.</p>
        `
      },
      '/training': {
        title: 'algoleague | Coding Training',
        description: 'algoleague offers coding training resources to help programmers improve and prepare for contests.',
        staticContent: `
          <h1>algoleague | Coding Training</h1>
          <p>Access coding training resources on algoleague to improve your algorithmic skills and prepare for programming competitions.</p>
        `
      },
      '/leaderboard': {
        title: 'algoleague | Leaderboard',
        description: 'View the algoleague leaderboard to see top performers in coding contests and challenges.',
        staticContent: `
          <h1>algoleague | Leaderboard</h1>
          <p>Check out the algoleague leaderboard to track top coders and your ranking in programming contests.</p>
        `
      },
      '/page/about-us': {
        title: 'algoleague | About Us',
        description: 'Learn about algoleague, a platform for coding contests, algorithmic problems, and training resources.',
        staticContent: `
          <h1>algoleague | About Us</h1>
          <p>algoleague is a platform dedicated to hosting coding contests, providing algorithmic problems, and offering training resources for programmers worldwide.</p>
        `
      },
       '/page/faq': {
        title: 'algoleague | FAQ',
        description: 'algoleague is a free online platform where you can test your algorithm knowledge by solving algorithm problems on various topics and levels, participate in algorithm competitions and prepare yourself for technical interviews.',
        staticContent: `
          <h1>algoleague | FAQ</h1>
          <p>algoleague is a free online platform where you can test your algorithm knowledge by solving algorithm problems on various topics and levels, participate in algorithm competitions and prepare yourself for technical interviews.</p>
        `
      },
          '/page/legal': {
        title: 'algoleague | legal',
        description: 'algoleague is an impartial platform away from political partisanship and political debates. As such, any statement regarding political commitment, particularly political propaganda, shall violate the algoleague Ethics Policy.',
        staticContent: `
          <h1>algoleague | legal</h1>
          <p>algoleague is an impartial platform away from political partisanship and political debates. As such, any statement regarding political commitment, particularly political propaganda, shall violate the algoleague Ethics Policy.</p>
        `
      },
      '/release-notes': {
        title: 'algoleague | Release Notes',
        description: 'See what changed on algoleague release by release: new features, fixes and improvements to contests, problems and training.',
        staticContent: `
          <h1>algoleague | Release Notes</h1>
          <p>See what changed on algoleague release by release: new features, fixes and improvements to contests, problems and training.</p>
        `
      },
      // Add similar entries for /page/help, /page/legal, /page/contact-us
      // Extend for dynamic pages (e.g., /training/<slug>/description, /contest/<slug>/description) via API
    };
    const data = seoData[url];
    if (data) {
      this.updateTitle(data.title);
      this.updateDescription(data.description);
    }
    // Always point the canonical at the page's own non-www URL. The static
    // index.html ships with canonical = homepage on every route, which tells
    // Google all pages are duplicates of the homepage.
    this.setCanonicalTag(`https://algoleague.com${url}`);
    return (data || seoData['/']).staticContent;
  }
}
